/**
 * Account Views — Active Accounts, Churn Accounts, Approval Queue.
 * Phase 11 (2026-06-22): 31-column table, beautiful sectioned detail, edit mode,
 * mark-as-churn (admin/manager/RM-owner), reactivate-churn (admin/manager).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, BadgeCheck, Building, Check, Edit2, FileSignature, Plus, RefreshCw,
  RotateCcw, Search, ShieldAlert, Users, X,
} from "lucide-react";
import { API } from "../../lib/api";
import { useDrilldown } from "./DrilldownContext";
import { fmtN, fmtDate } from "../../lib/format";

const fmtIDR = (v) =>
  v == null ? "—" :
  v >= 1_000_000_000 ? `Rp ${(v / 1_000_000_000).toFixed(1)} M` :
  v >= 1_000_000     ? `Rp ${(v / 1_000_000).toFixed(1)} Jt` :
  `Rp ${fmtN(v)}`;

const BENEFIT_TYPES = [
  "Evaluation Meeting Semester 1", "Evaluation Meeting Semester 2",
  "Event Marketing", "Sponsorship", "Entertainment",
  "Hampers Idul Fitri", "Hampers Natal", "Visit",
  "Parents Meeting", "Socialization Meeting", "TIE", "Cashback",
];

const CHURN_REASONS = [
  "Budget tidak memadai", "Pindah ke kompetitor", "Restrukturisasi sekolah",
  "Tidak puas dengan layanan", "Perubahan kurikulum", "Manajemen baru",
  "Hasil tidak sesuai ekspektasi", "Hubungan PIC menurun", "Lainnya",
];

// Helper: detail row (label : value)
function DetailRow({ label, value, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--text)", marginTop: 2, wordBreak: "break-word" }}>
        {value || <span style={{ color: "var(--muted)" }}>—</span>}
      </div>
    </div>
  );
}

function Section({ title, children, icon: Icon }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase",
                    letterSpacing: 1, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
        {Icon && <Icon size={12} />} {title}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12, padding: 12, background: "var(--surface-2)", borderRadius: 8 }}>
        {children}
      </div>
    </div>
  );
}

// ─────────── Account Detail (used by Active + Churn views) ───────────
function AccountDetail({ lead, currentUser, onClose, onUpdated, mode }) {
  // mode: "active" | "churn"
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(lead);
  const [showChurnForm, setShowChurnForm] = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);
  const [churn, setChurn] = useState({ reason: CHURN_REASONS[0], sub_reason: "", notes: "" });
  const [benefit, setBenefit] = useState({
    benefit_type: BENEFIT_TYPES[0], remark: "",
    tanggal: new Date().toISOString().slice(0, 10),
    dilakukan_via: "Offline", bertemu_dengan: "",
    pakai_budget: false, nominal_budget: "",
  });
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reactRm, setReactRm] = useState("");
  const [rmList, setRmList] = useState([]);

  useEffect(() => setForm(lead), [lead]);
  useEffect(() => {
    if (showReactivate && rmList.length === 0) {
      axios.get(`${API}/leads/meta/rms`).then((r) => setRmList(r.data?.rms || [])).catch(() => {});
    }
  }, [showReactivate, rmList.length]);

  const role = currentUser?.role;
  const isOwner = lead?.assigned_to === currentUser?.username;
  const canEdit = role === "admin" || role === "manager" || role === "sales_tl" || (role === "rm" && isOwner);
  const canMarkChurn = role === "admin" || role === "manager" || (role === "rm" && isOwner);
  const canReactivate = role === "admin" || role === "manager";

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = {};
      [
        "nama_account", "telepon_account", "npsn", "email_account", "alamat_account",
        "nama_yayasan", "lead_source", "transaction_type", "category",
        "jumlah_student", "jumlah_guru", "jumlah_guru_inggris", "tuition_fee",
        "english_exposure", "target_implementasi", "sifat_pelatihan",
        "course_package", "package_price_per_student", "course_length",
        "estimated_value", "existing_program", "competitor", "propinsi",
        "kabupaten", "tahun_ajaran",
      ].forEach((k) => { if (form[k] !== undefined) payload[k] = form[k]; });
      await axios.patch(`${API}/accounts/active/${lead.id}`, payload);
      setToast("✅ Data tersimpan");
      setEditing(false);
      onUpdated?.();
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast(`❌ ${e?.response?.data?.detail || "Gagal simpan"}`);
    } finally { setBusy(false); }
  };

  const submitChurn = async () => {
    if (!churn.reason) { setToast("❌ Alasan churn wajib"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/accounts/active/${lead.id}/mark-churn`, churn);
      setToast(`✅ Account dipindah ke Churn Account`);
      setShowChurnForm(false);
      setTimeout(() => { onUpdated?.(); onClose?.(); }, 1500);
    } catch (e) {
      setToast(`❌ ${e?.response?.data?.detail || "Gagal"}`);
    } finally { setBusy(false); }
  };

  const submitReactivate = async (target = "New Account Sign", assigned_rm = null) => {
    if (target === "New Account Sign" && !assigned_rm) {
      setToast("❌ Wajib pilih RM yang ditugaskan untuk reactivate ke New Account Sign");
      return;
    }
    setBusy(true);
    try {
      const body = { new_status: target };
      if (assigned_rm) body.assigned_rm = assigned_rm;
      await axios.post(`${API}/accounts/churn/${lead.id}/reactivate`, body);
      setToast(`✅ Account direactivate ke ${target}${assigned_rm ? ` (RM: @${assigned_rm})` : ""}`);
      setTimeout(() => { onUpdated?.(); onClose?.(); }, 1500);
    } catch (e) {
      setToast(`❌ ${e?.response?.data?.detail || "Gagal"}`);
    } finally { setBusy(false); }
  };

  const submitBenefit = async () => {
    if (!benefit.remark.trim()) { setToast("❌ Keterangan/remark wajib"); return; }
    if (benefit.dilakukan_via === "Offline" && !benefit.bertemu_dengan.trim()) {
      setToast("❌ Jika offline, wajib isi 'bertemu dengan siapa'"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/accounts/active/${lead.id}/benefit`, {
        benefit_type: benefit.benefit_type,
        remark: benefit.remark,
        tanggal: benefit.tanggal,
        dilakukan_via: benefit.dilakukan_via,
        bertemu_dengan: benefit.dilakukan_via === "Offline" ? benefit.bertemu_dengan : "",
        pakai_budget: benefit.pakai_budget,
        nominal_budget: benefit.pakai_budget ? Number(benefit.nominal_budget) || 0 : 0,
      });
      setToast(`✅ Aktivitas "${benefit.benefit_type}" tercatat`);
      setBenefit({ benefit_type: BENEFIT_TYPES[0], remark: "",
        tanggal: new Date().toISOString().slice(0, 10), dilakukan_via: "Offline",
        bertemu_dengan: "", pakai_budget: false, nominal_budget: "" });
      onUpdated?.();
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast(`❌ ${e?.response?.data?.detail || "Gagal"}`);
    } finally { setBusy(false); }
  };

  if (!lead) return null;
  const f = editing ? form : lead;
  const ipt = (k, label, type = "text") => (
    <div>
      <label style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, display: "block" }}>{label}</label>
      {editing ? (
        <input className="select-input" type={type} value={form[k] ?? ""}
          onChange={(e) => setForm({ ...form, [k]: type === "number" ? +e.target.value : e.target.value })}
          data-testid={`aa-edit-${k}`} />
      ) : (
        <div style={{ fontSize: 13, color: "var(--text)", marginTop: 2 }}>
          {f[k] != null && f[k] !== "" ? String(f[k]) : <span style={{ color: "var(--muted)" }}>—</span>}
        </div>
      )}
    </div>
  );

  const picStr = (p) => p ? `${p.nama || "—"} (${p.jabatan || "—"})${p.telepon ? " · " + p.telepon : ""}` : "—";

  return (
    <div className="modal-backdrop" data-testid="aa-detail-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1000,
               display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="card" style={{ width: "78vw", maxWidth: "78vw", maxHeight: "86vh",
                                       overflow: "auto", background: "var(--surface)" }}
           data-testid="aa-detail-card">
        <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 10,
                                          position: "sticky", top: 0, background: "var(--surface)", zIndex: 5 }}>
          <Building size={18} color="var(--accent)" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ fontSize: 16 }}>{lead.nama_account}</strong>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              NPSN {lead.npsn || "—"} · <span className="badge">{lead.status_lead}</span>
              {lead.assigned_to && <> · Owner @{lead.assigned_to}</>}
              {lead.merged_accounts_count > 1 && <> · <span className="badge" style={{ background: "rgba(46,134,222,.12)", color: "var(--accent)" }}>{lead.merged_accounts_count} record NPSN sama digabung</span></>}
            </div>
          </div>
          {/* Action buttons */}
          {mode === "active" && canEdit && !editing && (
            <button className="btn btn-ghost" onClick={() => setEditing(true)} data-testid="aa-edit-btn">
              <Edit2 size={12} /> Edit
            </button>
          )}
          {mode === "active" && canEdit && editing && (
            <>
              <button className="btn btn-ghost" onClick={() => { setEditing(false); setForm(lead); }}>Batal</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={busy} data-testid="aa-save-btn">
                <Check size={12} /> Simpan
              </button>
            </>
          )}
          {mode === "active" && canMarkChurn && !editing && (
            <button className="btn btn-ghost" onClick={() => setShowChurnForm(true)}
                    data-testid="aa-mark-churn-btn" style={{ color: "#B4392F" }}>
              <AlertTriangle size={12} /> Mark as Churn
            </button>
          )}
          {mode === "churn" && canReactivate && (
            <button className="btn btn-primary" onClick={() => setShowReactivate(true)}
                    data-testid="ca-reactivate-btn">
              <RotateCcw size={12} /> Reactivate
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose} data-testid="aa-detail-close"><X size={14} /></button>
        </div>

        {toast && <div className="alert ok" style={{ margin: 12 }} data-testid="aa-detail-toast">{toast}</div>}

        <div className="card-b" style={{ padding: 16 }}>
          {showChurnForm && (
            <div style={{ padding: 14, background: "rgba(231,76,60,.08)", border: "1px solid #FBD3CB",
                          borderRadius: 8, marginBottom: 14 }} data-testid="aa-churn-form">
              <h4 style={{ margin: "0 0 10px 0", color: "#B4392F" }}><AlertTriangle size={14} /> Form Churn Account</h4>
              <div className="field"><label>Alasan Churn *</label>
                <select className="select-input" value={churn.reason}
                  onChange={(e) => setChurn({ ...churn, reason: e.target.value })} data-testid="aa-churn-reason">
                  {CHURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="field" style={{ marginTop: 8 }}><label>Detail Sub-alasan (opsional)</label>
                <input className="select-input" value={churn.sub_reason}
                  onChange={(e) => setChurn({ ...churn, sub_reason: e.target.value })} data-testid="aa-churn-sub" />
              </div>
              <div className="field" style={{ marginTop: 8 }}><label>Catatan Tambahan</label>
                <textarea className="select-input" rows={2} value={churn.notes}
                  onChange={(e) => setChurn({ ...churn, notes: e.target.value })} data-testid="aa-churn-notes" />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                <button className="btn btn-ghost" onClick={() => setShowChurnForm(false)}>Batal</button>
                <button className="btn btn-primary" onClick={submitChurn} disabled={busy} data-testid="aa-churn-submit"
                  style={{ background: "#B4392F" }}>
                  <AlertTriangle size={12} /> Konfirmasi Churn
                </button>
              </div>
            </div>
          )}
          {showReactivate && (
            <div style={{ padding: 14, background: "rgba(39,174,96,.08)", border: "1px solid #B7E0C7",
                          borderRadius: 8, marginBottom: 14 }} data-testid="ca-reactivate-form">
              <h4 style={{ margin: "0 0 10px 0", color: "#1F7A4D" }}><RotateCcw size={14} /> Reactivate Account</h4>
              <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 10 }}>
                Pilih status tujuan + (untuk New Account Sign) RM yang akan ditugaskan:
              </p>
              <div style={{ padding: 10, background: "rgba(243,156,18,.08)", border: "1px solid #F5C772",
                              borderRadius: 8, marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#8B5A0F", display: "block", marginBottom: 6 }}>
                  🎯 RM yang ditugaskan (wajib untuk New Account Sign) — akan masuk KPI RM
                </label>
                <select className="select-input" value={reactRm}
                  onChange={(e) => setReactRm(e.target.value)} data-testid="ca-react-rm">
                  <option value="">— Pilih RM —</option>
                  {rmList.map((rm) => (
                    <option key={rm.username} value={rm.username}>
                      {rm.name} (@{rm.username}){rm.team ? ` · Team ${rm.team}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn btn-primary" onClick={() => submitReactivate("New Account Sign", reactRm)}
                  disabled={busy || !reactRm} data-testid="ca-reactivate-new">→ New Account Sign</button>
                <button className="btn btn-ghost" onClick={() => submitReactivate("Existing Account")} disabled={busy}>
                  → Existing Account
                </button>
                <button className="btn btn-ghost" onClick={() => { setShowReactivate(false); setReactRm(""); }}>Batal</button>
              </div>
            </div>
          )}

          {/* A. Account & Institution */}
          <Section title="A · Institution Profile" icon={Building}>
            {ipt("nama_account", "Nama Account / Institusi")}
            {ipt("telepon_account", "Nomor Telepon Sekolah")}
            {ipt("npsn", "NPSN")}
            {ipt("email_account", "Email Resmi Sekolah")}
            <div style={{ gridColumn: "1 / -1" }}>{ipt("alamat_account", "Alamat")}</div>
            {ipt("nama_yayasan", "Nama Yayasan")}
            {ipt("propinsi", "Propinsi")}
            {ipt("kabupaten", "Kota/Kabupaten")}
            {ipt("tahun_ajaran", "Tahun Ajaran")}
          </Section>

          {/* B. PIC & Decision Maker */}
          <Section title="B · PIC & Decision Maker" icon={Users}>
            <DetailRow label="PIC Utama" value={picStr(lead.pic_utama)} />
            <DetailRow label="PIC Tambahan" full
              value={(lead.pic_tambahan || []).map(picStr).join(" · ") || "—"} />
            <DetailRow label="Decision Maker"
              value={(lead.decision_makers || []).join(", ") + (lead.decision_maker_other ? " · " + lead.decision_maker_other : "")} />
          </Section>

          {/* C. Lead & Sales */}
          <Section title="C · Lead & Sales" icon={FileSignature}>
            {ipt("lead_source", "Lead Source")}
            <DetailRow label="Assigned Sales / Lead Owner" value={"@" + (lead.assigned_to || "—")} />
            <DetailRow label="Tanggal Lead Masuk" value={fmtDate(lead.created_at)} />
            <DetailRow label="Status Lead / Staging" value={<span className="badge">{lead.status_lead}</span>} />
          </Section>

          {/* D. Type & Category */}
          <Section title="D · Type & Category">
            {ipt("transaction_type", "Transaction Type")}
            {ipt("category", "Category Leads")}
          </Section>

          {/* E. Profile */}
          <Section title="E · Profile Sekolah">
            {ipt("jumlah_student", "Jumlah Student", "number")}
            {ipt("jumlah_guru", "Jumlah Guru / Trainer", "number")}
            {ipt("jumlah_guru_inggris", "Jumlah Guru Bahasa Inggris", "number")}
            {ipt("tuition_fee", "Tuition Fee", "number")}
            {ipt("english_exposure", "English Exposure")}
          </Section>

          {/* F. Needs */}
          <Section title="F · Needs & Expectation">
            <DetailRow label="Masalah Utama" full
              value={(lead.masalah_utama || []).join(", ") + (lead.masalah_utama_other ? " · " + lead.masalah_utama_other : "")} />
            <DetailRow label="Objective / Expected Outcome" full
              value={(lead.objectives || []).join(", ") + (lead.objectives_other ? " · " + lead.objectives_other : "")} />
            {ipt("target_implementasi", "Target Implementasi")}
            {ipt("sifat_pelatihan", "Sifat Pelatihan")}
          </Section>

          {/* G. Training */}
          <Section title="G · Training Package">
            {ipt("course_package", "Course Package")}
            {ipt("package_price_per_student", "Package Price / Student", "number")}
            {ipt("course_length", "Course Length per Level")}
            {ipt("estimated_value", "Estimated Deal Value", "number")}
          </Section>

          {/* H. Competitor */}
          <Section title="H · Competitor & Existing">
            {ipt("existing_program", "Existing Program")}
            {ipt("competitor", "Competitor")}
          </Section>

          {/* I. Signing & Renewal */}
          <Section title="I · Signing & Renewal Dates">
            <DetailRow label="Tanggal Signing" value={fmtDate(lead.tanggal_signing)} />
            <DetailRow label="Tanggal Berakhir Kerjasama" value={fmtDate(lead.tanggal_berakhir_kerjasama)} />
            <DetailRow label="Last Activity" value={fmtDate(lead.last_activity_at)} />
            <DetailRow label="Updated" value={fmtDate(lead.updated_at) + (lead.updated_by ? " · @" + lead.updated_by : "")} />
          </Section>

          {/* J. Churn Info — only if churn */}
          {mode === "churn" && (
            <Section title="J · Churn Info" icon={ShieldAlert}>
              <DetailRow label="Churn Reason" value={lead.churn_reason || lead.lost_reason || "—"} />
              <DetailRow label="Sub Reason" value={lead.churn_sub_reason} />
              <DetailRow label="Catatan Churn" full value={lead.churn_notes || lead.lost_notes} />
              <DetailRow label="Churn At" value={fmtDate(lead.churn_at)} />
              <DetailRow label="Churned By" value={lead.churn_by ? "@" + lead.churn_by : "—"} />
            </Section>
          )}

          {/* Add Activity form (active only) — admin/manager/RM-owner/Team Leader */}
          {mode === "active" && canEdit && !editing && (
            <div style={{ marginTop: 18, padding: 14, border: "1px solid var(--border-2)", borderRadius: 8 }}
                 data-testid="aa-activity-form">
              <h4 style={{ margin: "0 0 10px 0", fontSize: 13 }}><Plus size={12} /> Tambah Aktivitas pada Akun Ini</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div className="field"><label>Tanggal</label>
                  <input type="date" className="select-input" value={benefit.tanggal}
                    onChange={(e) => setBenefit({ ...benefit, tanggal: e.target.value })} data-testid="aa-act-tanggal" />
                </div>
                <div className="field"><label>Jenis Aktivitas</label>
                  <select className="select-input" value={benefit.benefit_type}
                    onChange={(e) => setBenefit({ ...benefit, benefit_type: e.target.value })} data-testid="aa-act-type">
                    {BENEFIT_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="field"><label>Online / Offline</label>
                  <select className="select-input" value={benefit.dilakukan_via}
                    onChange={(e) => setBenefit({ ...benefit, dilakukan_via: e.target.value })} data-testid="aa-act-via">
                    <option value="Offline">Offline</option>
                    <option value="Online">Online</option>
                  </select>
                </div>
                {benefit.dilakukan_via === "Offline" && (
                  <div className="field" style={{ gridColumn: "1 / -1" }}><label>Bertemu Dengan (siapa) *</label>
                    <input className="select-input" value={benefit.bertemu_dengan} placeholder="Nama & jabatan orang yang ditemui"
                      onChange={(e) => setBenefit({ ...benefit, bertemu_dengan: e.target.value })} data-testid="aa-act-bertemu" />
                  </div>
                )}
                <div className="field" style={{ gridColumn: "1 / -1" }}><label>Keterangan / Remark *</label>
                  <input className="select-input" value={benefit.remark} placeholder="Detail aktivitas..."
                    onChange={(e) => setBenefit({ ...benefit, remark: e.target.value })} data-testid="aa-act-remark" />
                </div>
                <div className="field">
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={benefit.pakai_budget}
                      onChange={(e) => setBenefit({ ...benefit, pakai_budget: e.target.checked })} data-testid="aa-act-budget-toggle" />
                    Pakai Budget?
                  </label>
                </div>
                {benefit.pakai_budget && (
                  <div className="field" style={{ gridColumn: "span 2" }}><label>Nominal Budget (Rp)</label>
                    <input type="number" min="0" className="select-input" value={benefit.nominal_budget} placeholder="0"
                      onChange={(e) => setBenefit({ ...benefit, nominal_budget: e.target.value })} data-testid="aa-act-budget-nominal" />
                  </div>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button className="btn btn-primary" onClick={submitBenefit} disabled={busy} data-testid="aa-benefit-submit">
                  <Check size={12} /> Simpan Aktivitas
                </button>
              </div>
            </div>
          )}

          {/* Activity history — rekap detail semua aktivitas akun */}
          {(lead.activities || []).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: 13 }}>📋 Rekap Aktivitas Akun ({lead.activities.length})</h4>
              <div style={{ maxHeight: 280, overflow: "auto", border: "1px solid var(--border-2)", borderRadius: 6 }}>
                <table className="data-table">
                  <thead><tr><th>Tanggal</th><th>Jenis</th><th>Via</th><th>Bertemu</th><th>Budget</th><th>Keterangan</th><th>By</th></tr></thead>
                  <tbody>
                    {(lead.activities || []).slice(-80).reverse().map((a, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{fmtDate(a.tanggal)}</td>
                        <td>{a.activity_type}</td>
                        <td style={{ fontSize: 11 }}>{a.dilakukan_via || "—"}</td>
                        <td style={{ fontSize: 11 }}>{a.bertemu_dengan || "—"}</td>
                        <td style={{ fontSize: 11 }}>{a.pakai_budget ? fmtIDR(a.nominal_budget || 0) : "—"}</td>
                        <td style={{ fontSize: 12 }}>{a.remark}</td>
                        <td>@{a.by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────── Active Account ───────────
export function ActiveAccountView({ currentUser }) {
  const [data, setData] = useState({ items: [], summary: {}, charts: { accounts_per_rm: [], complaints_per_rm: [] } });
  const [filters, setFilters] = useState({ sales: "", team: "", tahun_ajaran: "", npsn: "", nama_sekolah: "" });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [opts, setOpts] = useState({ nama_sales: [], tahun_ajaran: [], tim_sales: [] });
  const { openDrilldown } = useDrilldown();

  useEffect(() => {
    axios.get(`${API}/sales/filters`).then((r) => setOpts(r.data || {})).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try { const r = await axios.get(`${API}/accounts/active`, { params }); setData(r.data); }
    catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refreshAndKeepSelection = async () => {
    await load();
    if (selected) {
      try {
        const r = await axios.get(`${API}/accounts/active`, { params: { npsn: selected.npsn } });
        const updated = (r.data?.items || []).find((it) => it.id === selected.id);
        if (updated) setSelected(updated);
      } catch { /* noop */ }
    }
  };

  return (
    <div data-testid="active-account-view">
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <BadgeCheck size={14} color="var(--accent)" /> Active Accounts
        <span className="badge" style={{ marginLeft: 6, background: "rgba(46,134,222,.12)", color: "var(--accent)" }}>TeachCast Products</span>
        <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px" }}
          onClick={load} data-testid="aa-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>

      {/* Slicers */}
      <div className="card" style={{ marginTop: 10 }}>
        <div className="card-b" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr) auto", gap: 10, alignItems: "end" }}>
          {[
            ["sales", "Nama Sales", "select-sales"],
            ["team", "Team", "select-team"],
            ["tahun_ajaran", "Tahun Ajaran", "select-ta"],
            ["npsn", "NPSN", "text"],
            ["nama_sekolah", "Nama Sekolah", "text"],
          ].map(([k, label, type]) => {
            let options = null;
            if (type === "select-sales") options = opts.nama_sales || [];
            else if (type === "select-ta") options = opts.tahun_ajaran || [];
            else if (type === "select-team") options = (opts.tim_sales || []).filter((v) => v && v !== "—");
            return (
              <div className="field" key={k}>
                <label style={{ fontSize: 11, color: "var(--muted)" }}>{label}</label>
                {options ? (
                  <select className="select-input" value={filters[k]}
                    onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} data-testid={`aa-filter-${k}`}>
                    <option value="">Semua</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input className="select-input" value={filters[k]} placeholder="Semua"
                    onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} data-testid={`aa-filter-${k}`} />
                )}
              </div>
            );
          })}
          <button className="btn btn-primary" onClick={load} data-testid="aa-apply"><Search size={12} /> Terapkan</button>
        </div>
      </div>

      {/* KPI — klik untuk drill-down */}
      <div className="grid kpi-grid" style={{ marginTop: 16 }}>
        <div className="kpi-card" data-testid="aa-kpi-total" style={{ cursor: "pointer" }}
          onClick={() => openDrilldown({ title: "Active Accounts · Semua", staticRows: data.items, rowType: "leads" })}>
          <div className="label">TOTAL ACTIVE</div>
          <div className="value">{fmtN(data.summary.total_active || 0)}</div>
          <div className="sub">{data.summary.existing || 0} existing · {data.summary.new_sign || 0} new sign · klik →</div>
        </div>
        <div className="kpi-card" data-testid="aa-kpi-students" style={{ cursor: "pointer" }}
          onClick={() => openDrilldown({ title: "Active Accounts · Siswa per Akun", staticRows: [...data.items].sort((a, b) => (b.jumlah_student || 0) - (a.jumlah_student || 0)), rowType: "leads" })}>
          <div className="label">TOTAL STUDENTS</div>
          <div className="value">{fmtN(data.summary.total_students || 0)}</div>
          <div className="sub">Across active accounts · klik →</div>
        </div>
        <div className="kpi-card" data-testid="aa-kpi-value" style={{ cursor: "pointer" }}
          onClick={() => openDrilldown({ title: "Active Accounts · Portfolio Value", staticRows: [...data.items].sort((a, b) => (b.estimated_value || 0) - (a.estimated_value || 0)), rowType: "leads" })}>
          <div className="label">PORTFOLIO VALUE</div>
          <div className="value" style={{ fontSize: 22 }}>{fmtIDR(data.summary.total_value || 0)}</div>
          <div className="sub">Estimated total · klik →</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid row-split" style={{ marginTop: 14 }}>
        <div className="card">
          <div className="card-h"><strong>Jumlah Account per Relation Manager</strong></div>
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {data.charts.accounts_per_rm.length === 0 ? <div className="empty">Tidak ada data.</div> :
              data.charts.accounts_per_rm.map((r) => {
                const max = Math.max(...data.charts.accounts_per_rm.map((x) => x.count), 1);
                const w = Math.round((r.count / max) * 100);
                return (
                  <div key={r.username} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 110, fontSize: 12, fontWeight: 600 }}>@{r.username}</div>
                    <div style={{ flex: 1, background: "var(--surface-2)", borderRadius: 6, height: 22 }}>
                      <div style={{ width: `${w}%`, height: "100%", background: "var(--accent)",
                        borderRadius: 6, color: "#fff", padding: "0 10px", display: "flex",
                        alignItems: "center", fontSize: 12, fontWeight: 700 }}>{r.count}</div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div className="card">
          <div className="card-h"><strong>Complaints per RM · Solved vs Not</strong></div>
          <div className="card-b" style={{ padding: 0 }}>
            {data.charts.complaints_per_rm.length === 0 ? <div className="empty">Belum ada complaint tercatat.</div> :
              <table className="data-table">
                <thead><tr><th>RM</th><th style={{ color: "#10b981" }}>Solved</th><th style={{ color: "#ef4444" }}>Not Solved</th></tr></thead>
                <tbody>
                  {data.charts.complaints_per_rm.map((r) => (
                    <tr key={r.username}><td>@{r.username}</td><td>{r.solved || 0}</td><td>{r.not_solved || 0}</td></tr>
                  ))}
                </tbody>
              </table>
            }
          </div>
        </div>
      </div>

      {/* Account list — expanded columns */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center" }}>
          <Building size={14} color="var(--accent)" />
          <strong style={{ marginLeft: 8 }}>Account List · {data.items.length} accounts</strong>
          <span className="badge" style={{ marginLeft: "auto" }}>Click row for detail</span>
        </div>
        <div className="card-b" style={{ padding: 0, overflowX: "auto" }}>
          {loading ? <div className="empty">Memuat...</div> :
            data.items.length === 0 ? <div className="empty">Tidak ada account.</div> :
              <table className="data-table data-table-wide" data-testid="aa-table" style={{ width: "100%", minWidth: 1700 }}>
                <thead><tr>
                  <th>Nama Account</th><th>NPSN</th><th>Telepon</th><th>Email</th>
                  <th>Yayasan</th><th>PIC Utama</th><th>Lead Source</th>
                  <th>Lead Owner</th><th>Status</th><th>Transaction</th>
                  <th>Category</th><th>Propinsi</th><th>Kota/Kab</th><th>Siswa</th><th>Tuition Fee</th>
                  <th>Course Package</th><th>Pkg Price/Std</th><th>Est. Value</th>
                  <th>Existing</th><th>Competitor</th><th>Berakhir</th>
                  <th className="col-action">Aksi</th>
                </tr></thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={it.id} style={{ cursor: "pointer" }} onClick={() => setSelected(it)}
                        data-testid={`aa-row-${it.id}`}>
                      <td><strong>{it.nama_account}</strong>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>{it.alamat_account?.slice(0, 40) || ""}</div>
                      </td>
                      <td>{it.npsn || "—"}</td>
                      <td style={{ fontSize: 11 }}>{it.telepon_account || "—"}</td>
                      <td style={{ fontSize: 11 }}>{it.email_account || "—"}</td>
                      <td>{it.nama_yayasan || "—"}</td>
                      <td style={{ fontSize: 11 }}>{it.pic_utama?.nama || "—"}</td>
                      <td>{it.lead_source || "—"}</td>
                      <td>@{it.assigned_to}</td>
                      <td><span className="badge">{it.status_lead}</span></td>
                      <td style={{ fontSize: 11 }}>{it.transaction_type || "—"}</td>
                      <td style={{ fontSize: 11 }}>{it.category || "—"}</td>
                      <td style={{ fontSize: 11 }}>{it.propinsi || "—"}</td>
                      <td style={{ fontSize: 11 }}>{it.kabupaten || "—"}</td>
                      <td>{fmtN(it.jumlah_student || 0)}</td>
                      <td>{fmtIDR(it.tuition_fee || 0)}</td>
                      <td style={{ fontSize: 11 }}>{it.course_package || "—"}</td>
                      <td>{fmtIDR(it.package_price_per_student || 0)}</td>
                      <td>{fmtIDR(it.estimated_value || 0)}</td>
                      <td style={{ fontSize: 11 }}>{it.existing_program || "—"}</td>
                      <td style={{ fontSize: 11 }}>{it.competitor || "—"}</td>
                      <td style={{ fontSize: 11 }}>{fmtDate(it.tanggal_berakhir_kerjasama)}</td>
                      <td className="col-action" onClick={(e) => e.stopPropagation()}>
                        <button className="btn-row-edit"
                          onClick={() => setSelected(it)} data-testid={`aa-edit-${it.id}`}>
                          <Edit2 size={10} /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      </div>

      {selected && <AccountDetail lead={selected} currentUser={currentUser} mode="active"
        onClose={() => setSelected(null)} onUpdated={refreshAndKeepSelection} />}
    </div>
  );
}

// ─────────── Churn Account ───────────
export function ChurnAccountView({ currentUser }) {
  const [data, setData] = useState({ items: [], summary: {}, reasons: [] });
  const [filters, setFilters] = useState({ npsn: "", nama_sekolah: "" });
  const [selected, setSelected] = useState(null);
  const { openDrilldown } = useDrilldown();

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try { const r = await axios.get(`${API}/accounts/churn`, { params }); setData(r.data); }
    catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const churnItems = (data.items || []).filter((it) => it.status_lead === "Churn Account");
  const lostItems = (data.items || []).filter((it) => it.status_lead === "Lost Lead");
  const churnStudents = churnItems.reduce((s, it) => s + (Number(it.jumlah_student) || 0), 0);

  const renderTable = (items, kind) => (
    <div className="card-b" style={{ padding: 0, overflowX: "auto" }}>
      {items.length === 0 ? <div className="empty">Tidak ada data.</div> :
        <table className="data-table data-table-wide" data-testid={`ca-table-${kind}`} style={{ width: "100%", minWidth: 1400 }}>
          <thead><tr>
            <th>Nama Sekolah</th><th>NPSN</th><th>Telepon</th><th>Yayasan</th><th>Propinsi</th><th>Kota/Kab</th>
            <th>Status</th><th>RM/Sales</th><th>Course Package</th>
            <th>{kind === "churn" ? "Alasan Churn" : "Alasan Lost"}</th><th>Tanggal</th><th>Siswa</th><th>Est. Value</th>
            <th className="col-action">Aksi</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ cursor: "pointer" }} onClick={() => setSelected(it)} data-testid={`ca-row-${it.id}`}>
                <td><strong>{it.nama_account}</strong></td>
                <td>{it.npsn || "—"}</td>
                <td style={{ fontSize: 11 }}>{it.telepon_account || "—"}</td>
                <td style={{ fontSize: 11 }}>{it.nama_yayasan || "—"}</td>
                <td style={{ fontSize: 11 }}>{it.propinsi || "—"}</td>
                <td style={{ fontSize: 11 }}>{it.kabupaten || "—"}</td>
                <td><span className="badge" style={{ background: kind === "churn" ? "rgba(142,68,173,0.15)" : "rgba(231,76,60,0.15)", color: kind === "churn" ? "#8E44AD" : "#B4392F" }}>{it.status_lead}</span></td>
                <td>@{it.assigned_to}</td>
                <td style={{ fontSize: 11 }}>{it.course_package || "—"}</td>
                <td style={{ fontSize: 11 }}>{it.churn_reason || it.lost_reason || "—"}</td>
                <td style={{ fontSize: 11 }}>{fmtDate(it.churn_at || it.updated_at)}</td>
                <td>{fmtN(it.jumlah_student || 0)}</td>
                <td>{fmtIDR(it.estimated_value || 0)}</td>
                <td className="col-action" onClick={(e) => e.stopPropagation()}>
                  <button className="btn-row-edit" onClick={() => setSelected(it)} data-testid={`ca-edit-${it.id}`}>
                    <Edit2 size={10} /> Detail
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
    </div>
  );

  return (
    <div data-testid="churn-account-view">
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ShieldAlert size={14} color="var(--danger)" /> Churn & Lost Lead
        <span className="badge" style={{ marginLeft: 6, fontSize: 10 }}>Churn ≠ Lost Lead (dibedakan)</span>
        <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px" }}
          onClick={load} data-testid="ca-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>

      <div className="card" style={{ marginTop: 10 }}>
        <div className="card-b" style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
          <div className="field"><label>NPSN</label>
            <input className="select-input" value={filters.npsn} placeholder="Cari NPSN..."
              onChange={(e) => setFilters({ ...filters, npsn: e.target.value })} data-testid="ca-filter-npsn" />
          </div>
          <div className="field"><label>Nama Sekolah</label>
            <input className="select-input" value={filters.nama_sekolah} placeholder="Cari nama..."
              onChange={(e) => setFilters({ ...filters, nama_sekolah: e.target.value })} data-testid="ca-filter-nama" />
          </div>
          <button className="btn btn-primary" onClick={load} data-testid="ca-apply"><Search size={12} /> Terapkan</button>
        </div>
      </div>

      <div className="grid kpi-grid" style={{ marginTop: 16 }}>
        <div className="kpi-card" data-testid="ca-kpi-total" style={{ cursor: "pointer" }}
          onClick={() => openDrilldown({ title: "Churn Account · Detail", staticRows: churnItems, rowType: "leads" })}>
          <div className="label">CHURN ACCOUNT</div>
          <div className="value">{fmtN(churnItems.length)}</div>
          <div className="sub">Pernah aktif, lalu kontrak berakhir / putus kerjasama · klik →</div>
        </div>
        <div className="kpi-card" data-testid="ca-kpi-lost" style={{ borderColor: "#E74C3C55", cursor: "pointer" }}
          onClick={() => openDrilldown({ title: "Lost Lead · Detail", staticRows: lostItems, rowType: "leads" })}>
          <div className="label">LOST LEAD</div>
          <div className="value" style={{ color: "#E74C3C" }}>{fmtN(lostItems.length)}</div>
          <div className="sub">Batal / tidak jadi SEBELUM bergabung (bukan churn) · klik →</div>
        </div>
        <div className="kpi-card" data-testid="ca-kpi-students" style={{ cursor: "pointer" }}
          onClick={() => openDrilldown({ title: "Churn · Siswa Hilang", staticRows: [...churnItems].sort((a, b) => (b.jumlah_student || 0) - (a.jumlah_student || 0)), rowType: "leads" })}>
          <div className="label">STUDENTS LOST (CHURN)</div>
          <div className="value">{fmtN(churnStudents)}</div>
          <div className="sub">Siswa dari akun churn · klik →</div>
        </div>
      </div>

      {data.reasons.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-h"><strong>Alasan Churn / Lost</strong></div>
          <div className="card-b" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {data.reasons.map((r) => (
              <span key={r.reason} className="badge"
                    style={{ fontSize: 12, padding: "6px 10px", background: "rgba(231,76,60,0.1)", color: "#B4392F" }}>
                {r.reason} · {r.count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center" }}>
          <ShieldAlert size={14} color="#8E44AD" />
          <strong style={{ marginLeft: 8 }}>Churn Account · {churnItems.length}</strong>
          <span className="badge" style={{ marginLeft: "auto", background: "rgba(142,68,173,.12)", color: "#8E44AD" }}>
            Pernah aktif lalu berhenti
          </span>
        </div>
        {renderTable(churnItems, "churn")}
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center" }}>
          <ShieldAlert size={14} color="#E74C3C" />
          <strong style={{ marginLeft: 8 }}>Lost Lead · {lostItems.length}</strong>
          <span className="badge" style={{ marginLeft: "auto", background: "rgba(231,76,60,.12)", color: "#B4392F" }}>
            Batal sebelum bergabung (bukan churn)
          </span>
        </div>
        {renderTable(lostItems, "lost")}
      </div>

      {selected && <AccountDetail lead={selected} currentUser={currentUser} mode="churn"
        onClose={() => setSelected(null)} onUpdated={load} />}
    </div>
  );
}

// ─────────── Approval ───────────
export function ApprovalView() {
  const [pwReqs, setPwReqs] = useState([]);
  const [leadReqs, setLeadReqs] = useState([]);
  const [actReqs, setActReqs] = useState([]);
  const [toast, setToast] = useState(null);
  const [comment, setComment] = useState({});

  const load = async () => {
    try { const r = await axios.get(`${API}/auth/admin/password-requests`); setPwReqs(r.data?.items || []); }
    catch { /* not admin */ }
    try { const r2 = await axios.get(`${API}/workflow/approvals/lead-promotions`); setLeadReqs(r2.data?.items || []); }
    catch { /* not approver */ }
    try { const r3 = await axios.get(`${API}/workflow/approvals/account-activities`); setActReqs(r3.data?.items || []); }
    catch { /* not approver */ }
  };
  useEffect(() => { load(); }, []);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 4000); };

  const handlePw = async (id, action) => {
    try { await axios.post(`${API}/auth/admin/password-requests/${id}/${action}`); flash(`✅ ${action}d`); load(); }
    catch (e) { flash(`❌ ${e?.response?.data?.detail || "Gagal"}`); }
  };
  const handleLead = async (id, action) => {
    try {
      await axios.post(`${API}/workflow/approvals/lead-promotions/${id}/decide`, {
        action, comment: comment[id] || "",
      });
      flash(`✅ Lead promotion ${action}d`);
      load();
    } catch (e) { flash(`❌ ${e?.response?.data?.detail || "Gagal"}`); }
  };
  const handleAct = async (id, action) => {
    try {
      await axios.post(`${API}/workflow/approvals/account-activities/${id}/decide`, { action });
      flash(`✅ Activity ${action}d`);
      load();
    } catch (e) { flash(`❌ ${e?.response?.data?.detail || "Gagal"}`); }
  };

  return (
    <div data-testid="approval-view">
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <FileSignature size={14} color="var(--accent)" /> Approval Center
        <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px" }} onClick={load}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      {toast && <div className="alert ok" style={{ margin: "8px 0" }} data-testid="ap-toast">{toast}</div>}

      {/* Lead Promotion Requests */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Users size={14} color="var(--accent)" />
          <strong>Lead Promotion Requests (→ New Account Sign)</strong>
          <span className="badge" style={{ marginLeft: "auto" }}>{leadReqs.length} pending</span>
        </div>
        <div className="card-b" style={{ padding: 0 }}>
          {leadReqs.length === 0 ? <div className="empty">Tidak ada permintaan promosi.</div> :
            <table className="data-table" data-testid="ap-lead-promo-table">
              <thead><tr><th>Account</th><th>Submitter</th><th>Target RM</th><th>Signing</th><th>Note</th><th>Comment</th><th>Aksi</th></tr></thead>
              <tbody>
                {leadReqs.map((q) => (
                  <tr key={q.id}>
                    <td><strong>{q.nama_account}</strong>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{q.current_status} → {q.target_status}</div>
                    </td>
                    <td>@{q.submitted_by}<br/><span style={{ fontSize: 10, color: "var(--muted)" }}>{q.submitter_role}</span></td>
                    <td>@{q.assigned_rm}</td>
                    <td style={{ fontSize: 11 }}>{q.tanggal_signing || "—"}</td>
                    <td style={{ fontSize: 11 }}>{q.notes || "—"}</td>
                    <td>
                      <input className="select-input" placeholder="Komentar..." style={{ fontSize: 11, padding: "4px 6px" }}
                        value={comment[q.id] || ""}
                        onChange={(e) => setComment({ ...comment, [q.id]: e.target.value })}
                        data-testid={`ap-lead-comment-${q.id}`} />
                    </td>
                    <td style={{ display: "flex", gap: 4 }}>
                      <button className="btn btn-primary" style={{ padding: "3px 6px", fontSize: 10 }}
                        onClick={() => handleLead(q.id, "approve")} data-testid={`ap-lead-approve-${q.id}`}>
                        <Check size={10} /> Approve
                      </button>
                      <button className="btn btn-ghost" style={{ padding: "3px 6px", fontSize: 10 }}
                        onClick={() => handleLead(q.id, "revise")} data-testid={`ap-lead-revise-${q.id}`}>
                        Revisi
                      </button>
                      <button className="btn btn-ghost" style={{ padding: "3px 6px", fontSize: 10, color: "#B4392F" }}
                        onClick={() => handleLead(q.id, "reject")} data-testid={`ap-lead-reject-${q.id}`}>
                        <X size={10} /> Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>

      {/* Activity Approval Requests */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <strong>Account Activity Requests</strong>
          <span className="badge" style={{ marginLeft: "auto" }}>{actReqs.length} pending</span>
        </div>
        <div className="card-b" style={{ padding: 0 }}>
          {actReqs.length === 0 ? <div className="empty">Tidak ada permintaan aktivitas akun.</div> :
            <table className="data-table" data-testid="ap-activity-table">
              <thead><tr><th>Account</th><th>Activity</th><th>Remark</th><th>Submitter</th><th>RM Owner</th><th>Aksi</th></tr></thead>
              <tbody>
                {actReqs.map((a) => (
                  <tr key={a.id}>
                    <td><strong>{a.nama_account}</strong></td>
                    <td>{a.activity?.activity_type}</td>
                    <td style={{ fontSize: 11 }}>{a.activity?.remark}</td>
                    <td>@{a.submitted_by}</td>
                    <td>@{a.lead_owner_rm}</td>
                    <td style={{ display: "flex", gap: 4 }}>
                      <button className="btn btn-primary" style={{ padding: "3px 6px", fontSize: 10 }}
                        onClick={() => handleAct(a.id, "approve")} data-testid={`ap-act-approve-${a.id}`}>
                        <Check size={10} /> Approve
                      </button>
                      <button className="btn btn-ghost" style={{ padding: "3px 6px", fontSize: 10, color: "#B4392F" }}
                        onClick={() => handleAct(a.id, "reject")} data-testid={`ap-act-reject-${a.id}`}>
                        <X size={10} /> Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>

      {/* Password Change Requests */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center" }}>
          <strong>Password Change Requests</strong>
          <span className="badge" style={{ marginLeft: "auto" }}>{pwReqs.length} pending</span>
        </div>
        <div className="card-b" style={{ padding: 0 }}>
          {pwReqs.length === 0 ? <div className="empty">Tidak ada permintaan password yang menunggu.</div> :
            <table className="data-table" data-testid="ap-pw-table">
              <thead><tr><th>User</th><th>Role</th><th>Alasan</th><th>Waktu</th><th>Aksi</th></tr></thead>
              <tbody>
                {pwReqs.map((p) => (
                  <tr key={p._id}>
                    <td><strong>@{p.username}</strong></td>
                    <td>{p.role}</td>
                    <td style={{ fontSize: 12 }}>{p.reason || "—"}</td>
                    <td style={{ fontSize: 11 }}>{fmtDate(p.created_at)}</td>
                    <td style={{ display: "flex", gap: 6 }}>
                      <button className="btn btn-primary" style={{ padding: "4px 8px", fontSize: 11 }}
                        onClick={() => handlePw(p._id, "approve")} data-testid={`ap-approve-${p._id}`}>
                        <Check size={11} /> Approve
                      </button>
                      <button className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 11 }}
                        onClick={() => handlePw(p._id, "reject")} data-testid={`ap-reject-${p._id}`}>
                        <X size={11} /> Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>
    </div>
  );
}
